/* ═══════════════════════════════════════════════════════════════
   The Designer — node views for the non-prose blocks.

   Figures and smart tokens are single blocks in the flow. They render
   as the printed thing (the real image, the resolved list), never as a
   placeholder line, because the paper IS the editor — the one exception
   is Related sections in a master, which has no project to list yet.

   Tables are REAL TipTap table nodes as of §4 R7 (contract) — their
   NodeView (`SpecTableView`, visual-only: colgroup %, border/shading
   CSS vars) lives in tables.ts next to the rest of the table feature,
   wired in automatically by the framework's own columnResizing plugin.
   Nothing left to do here for tables.
   ═══════════════════════════════════════════════════════════════ */

import type { Node as PMNode } from '@tiptap/pm/model';
import type { NodeView } from '@tiptap/pm/view';
import {
  RELATED_SECTIONS_KIND,
  newRelatedCount,
  readRelatedParams,
  relatedSectionLines,
} from '../../shared/related-sections';
import type { RelatedSectionsHost, RelatedSectionsSnapshot } from './relatedSectionsStore';

/** A figure, at the width it will print. */
export class SpecImageView implements NodeView {
  dom: HTMLElement;

  constructor(node: PMNode) {
    this.dom = document.createElement('div');
    this.dom.className = 'dsg-image-wrap';
    const data = node.attrs.imageData as string;
    if (data) {
      const img = document.createElement('img');
      img.src = data;
      img.alt = (node.attrs.text as string) || 'Figure';
      img.style.width = `${Math.max(20, Math.min(100, (node.attrs.imageWidth as number) ?? 100))}%`;
      this.dom.appendChild(img);
    } else {
      const fallback = document.createElement('div');
      fallback.className = 'dsg-atom-fallback';
      fallback.textContent = 'Figure — image data missing';
      this.dom.appendChild(fallback);
    }
  }

  stopEvent() {
    return false;
  }
}

/** A smart token. Two shapes:
 *
 *  · Related sections (the one smart block that returned — Josh 2026-10-02):
 *    with a host (the Designer, a project spec) it draws the LIVE list the
 *    export will print, as read-only lines at the paragraph's level, and a
 *    click opens the picker. With no host (Spec Studio — a master or variant
 *    has no project) it is a placeholder that says what it will become.
 *  · Every other kind is a legacy token: resolved content, read-only (smart
 *    block authoring is cut — §3.4), with a quiet chip so the writer knows it
 *    refreshes itself at export. */
export class SmartBlockView implements NodeView {
  dom: HTMLElement;
  private node: PMNode;
  private readonly host: RelatedSectionsHost | null;
  private unsubscribe: (() => void) | null = null;

  constructor(node: PMNode, host?: RelatedSectionsHost | null) {
    this.node = node;
    this.dom = document.createElement('div');
    this.host = isRelated(node) ? (host ?? null) : null;
    if (!isRelated(node)) {
      renderLegacy(this.dom, node);
      return;
    }
    if (!this.host) {
      renderPlaceholder(this.dom);
      return;
    }
    const live = this.host;
    // Classes are ADDED, never assigned, from here on: ProseMirror's own
    // selected-node class and the level decoration's classes live on this
    // same element and a redraw must not wipe them.
    this.dom.classList.add('dsg-smart', 'dsg-related');
    this.unsubscribe = live.subscribe(() => this.render());
    // A click opens the picker — for a reader too (their checkboxes are
    // disabled), which is why this is not left to ProseMirror's node
    // selection alone: a read-only editor never selects the node.
    this.dom.addEventListener('click', () => live.open(this.blockId(), this.dom, false));
    this.render();
  }

  private blockId(): string {
    return (this.node.attrs.blockId as string) || '';
  }

  private render() {
    if (this.host) renderRelated(this.dom, this.node, this.host.get());
  }

  /** Only the live list re-renders in place (so an open picker keeps its
   *  anchor); every other shape is rebuilt by ProseMirror, as it always was. */
  update(node: PMNode): boolean {
    if (!this.host || node.type !== this.node.type || !isRelated(node)) return false;
    this.node = node;
    this.render();
    return true;
  }

  /** ProseMirror's own default (selected class + draggable, so a picked block
   *  still drags), plus: selecting a live Related sections block (a click, an
   *  insert, arrowing onto it) shows its picker. Focus stays in the document
   *  until the writer presses Enter. */
  selectNode() {
    this.dom.classList.add('ProseMirror-selectednode');
    this.dom.draggable = true;
    if (this.host) this.host.open(this.blockId(), this.dom, false);
  }

  deselectNode() {
    this.dom.classList.remove('ProseMirror-selectednode');
    this.dom.removeAttribute('draggable');
  }

  stopEvent() {
    return false;
  }

  destroy() {
    this.unsubscribe?.();
  }
}

function isRelated(node: PMNode): boolean {
  const meta = node.attrs.meta as Record<string, unknown> | undefined;
  return meta?.smartBlockKind === RELATED_SECTIONS_KIND;
}

function renderLegacy(dom: HTMLElement, node: PMNode) {
  dom.className = 'dsg-smart';
  const body = document.createElement('span');
  body.className = 'dsg-smart-text';
  body.textContent = (node.attrs.text as string) || '';
  const chip = document.createElement('span');
  chip.className = 'dsg-smart-chip';
  const kind =
    ((node.attrs.meta as Record<string, unknown>)?.smartBlockKind as string) || 'legacy token';
  chip.textContent = kind.replace(/-/g, ' ');
  chip.title = 'Legacy token — resolved from project data at export';
  dom.appendChild(body);
  dom.appendChild(chip);
}

/** Master / variant: no project, so nothing to list yet. */
function renderPlaceholder(dom: HTMLElement) {
  dom.className = 'dsg-smart dsg-related dsg-related-placeholder';
  dom.setAttribute('aria-label', 'Related sections block');
  dom.appendChild(listChecksIcon());
  const body = document.createElement('span');
  body.className = 'dsg-related-ph-text';
  const name = document.createElement('span');
  name.className = 'dsg-related-ph-name';
  name.textContent = 'Related sections';
  const hint = document.createElement('span');
  hint.className = 'dsg-related-muted';
  hint.textContent = 'Lists the other sections once this spec is in a project.';
  body.appendChild(name);
  body.appendChild(hint);
  dom.appendChild(body);
}

/** Project spec: the live list, exactly as it will print. Redraws the
 *  children only — the element's own classes belong to ProseMirror too. */
function renderRelated(dom: HTMLElement, node: PMNode, snap: RelatedSectionsSnapshot) {
  dom.replaceChildren();
  const params = readRelatedParams(
    (node.attrs.meta as Record<string, unknown> | undefined)?.smartParams
  );
  const ready = snap.status === 'ready';
  const lines = ready ? relatedSectionLines(params, snap.candidates, snap.key) : [];
  const fresh = ready ? newRelatedCount(params, snap.candidates, snap.key) : 0;
  dom.title = snap.canEdit ? 'Related sections — click to choose' : 'Related sections';
  dom.setAttribute(
    'aria-label',
    `Related sections: ${lines.length} listed${fresh ? `, ${fresh} new` : ''}`
  );

  const list = document.createElement('div');
  list.className = 'dsg-related-list';
  if (!ready) {
    list.setAttribute('aria-busy', 'true');
    for (let i = 0; i < 2; i++) {
      const sk = document.createElement('div');
      sk.className = 'dsg-related-skel';
      list.appendChild(sk);
    }
  } else if (!snap.candidates.length) {
    list.appendChild(muted('No other sections in this project yet.'));
  } else if (!lines.length) {
    list.appendChild(muted('No sections included — click to choose.'));
  } else {
    for (const text of lines) {
      const line = document.createElement('div');
      line.className = 'dsg-related-line';
      line.textContent = text;
      list.appendChild(line);
    }
  }
  dom.appendChild(list);

  // Editor chrome only — never exported (the export resolves the params,
  // not this view).
  if (fresh > 0) {
    const chip = document.createElement('span');
    chip.className = 'dsg-smart-chip dsg-related-new';
    chip.textContent = `${fresh} new`;
    chip.title = `${fresh} section${fresh === 1 ? ' was' : 's were'} added to the project since this list was last opened`;
    dom.appendChild(chip);
  }
}

function muted(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'dsg-related-muted';
  el.textContent = text;
  return el;
}

/** lucide `list-checks`, drawn as plain SVG because a node view is DOM, not
 *  React — the same glyph the insert buttons render through the kit Icon. */
function listChecksIcon(): SVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'dsg-related-icon');
  for (const d of LIST_CHECKS_PATHS) {
    const p = document.createElementNS(NS, 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
  }
  return svg;
}

const LIST_CHECKS_PATHS = ['M13 5h8', 'M13 12h8', 'M13 19h8', 'm3 17 2 2 4-4', 'm3 7 2 2 4-4'];
